import { Link, useParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import {
  formatBytes,
  formatLongDateOrDash,
} from "@shared-core/domain/formatters";
import { useListQuery } from "@shared-core/domain/useListQuery";
import type { CustomerArtworkPreflightIssue } from "@shared-core/data/customer-api-types";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import { usePortalViewer } from "./PortalViewerContext";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  FileText,
  Info,
  XCircle,
} from "lucide-react";

// Artwork preflight check journey (verdict) — the design that matters: what
// we found, put in language a customer can act on, without exposing the
// engine's own blocking/fixable split as the organising idea.
//
// The backend words every finding and gives it its tier and the check its
// verdict (order to invoice doc 66 §7, doc 60 §4.4): this page only groups
// what it is sent into three customer-facing sections, deliberately in this
// order:
//   1. "We can't print this" — must re-export. Never buried, always first.
//   2. "We'll sort this for you" — auto-fixable, reassuring tone.
//   3. "Worth knowing" — fixable but not auto-corrected; the print will
//      differ from what the customer expects.
// A clean file (no issues at all) gets its own "good to go" state rather
// than three empty sections — see the brief: a clean result must feel
// clean, not read as an empty results table. A file held for our team (a
// check the prepress team has no wording for yet) shows only the backend's
// neutral status: no finding, no check name (Ben, 5 Oct).
//
// Wired to the real backend via CustomerDataAdapter.getArtworkCheck (GET
// /customer/artwork-checks/{check_id}) — see PortalArtworkCheckPage.tsx's
// header for the submit side and why the error state matters most today.

type Tier = "cant_print" | "we_will_sort" | "worth_knowing";

/** The issues of one tier that carry a sentence (an unrecognised one never reaches a customer's read). */
function sentencesOf(
  issues: CustomerArtworkPreflightIssue[],
  tier: Tier,
): CustomerArtworkPreflightIssue[] {
  return issues.filter((i) => i.tier === tier && i.sentence !== null);
}

/** The "we can print this" banner's lead-in, counted from the findings listed below it so the
 *  heading never promises more than the list delivers: one, a couple (exactly two), a few. */
function thingsToKnowHeading(findingCount: number): string {
  const lead =
    findingCount === 1
      ? "One thing"
      : findingCount === 2
        ? "A couple of things"
        : "A few things";
  return `${lead} to know before we do — see below.`;
}

function IssueRow({ issue }: { issue: CustomerArtworkPreflightIssue }) {
  return (
    <div className="space-y-1 border-b border-muted pb-3 text-sm last:border-0 last:pb-0">
      {issue.hit_count > 1 && (
        <p className="text-xs text-muted-foreground">
          Found in {issue.hit_count} places
        </p>
      )}
      <p>{issue.sentence}</p>
    </div>
  );
}

function IssueGroupCard({
  title,
  icon: Icon,
  tone,
  issues,
}: {
  title: string;
  icon: typeof XCircle;
  tone: "critical" | "positive" | "neutral";
  issues: CustomerArtworkPreflightIssue[];
}) {
  if (issues.length === 0) return null;
  const toneClasses = {
    critical: "border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/30",
    positive:
      "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/20",
    neutral: "border-muted bg-muted/20",
  }[tone];
  const iconClasses = {
    critical: "text-red-600 dark:text-red-400",
    positive: "text-green-700 dark:text-green-400",
    neutral: "text-muted-foreground",
  }[tone];

  return (
    <Card className={`space-y-3 border p-5 ${toneClasses}`}>
      <div className="flex items-center gap-2">
        <Icon className={`size-5 shrink-0 ${iconClasses}`} />
        <p className="font-medium">{title}</p>
      </div>
      <div className="space-y-3">
        {issues.map((issue, index) => (
          <IssueRow key={`${issue.check_name}-${index}`} issue={issue} />
        ))}
      </div>
    </Card>
  );
}

export function PortalArtworkCheckResultPage() {
  const { checkId } = useParams<{ checkId: string }>();
  const adapter = useCustomerDataAdapter();
  const { viewKey, buildPortalUrl } = usePortalViewer();

  // getArtworkCheck takes no viewer parameter (scope is always implicit —
  // see CustomerDataAdapter's own header), but `viewKey` still belongs in
  // the dep array so switching the viewer (the review persona, or the customer chosen) refetches under the new
  // scope, same convention as PortalOrderDetailPage.tsx's getOrderContext.
  const { state, retry } = useListQuery(
    () => adapter.getArtworkCheck(checkId ?? ""),
    [adapter, viewKey, checkId],
  );

  if (state.status === "loading") {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        Loading your result…
      </Card>
    );
  }

  if (state.status === "error") {
    return (
      <Card className="space-y-3 p-8 text-center">
        <AlertTriangle className="mx-auto size-6 text-muted-foreground" />
        <p className="font-medium">We couldn&apos;t load this result</p>
        <p className="text-sm text-muted-foreground">{state.message}</p>
        <Button variant="outline" onClick={retry}>
          Try again
        </Button>
      </Card>
    );
  }

  const verdict = state.data;

  if (!verdict) {
    return (
      <Card className="space-y-3 p-8 text-center">
        <p className="font-medium">We can&apos;t show you this result</p>
        <p className="text-sm text-muted-foreground">
          It either doesn&apos;t exist, or it isn&apos;t one of your checks.
        </p>
        <div>
          <Button asChild variant="outline">
            <Link to={buildPortalUrl(paths.portalArtworkCheck())}>
              Check another file
            </Link>
          </Button>
        </div>
      </Card>
    );
  }

  const outcome = verdict.verdict;

  return (
    <div className="space-y-5">
      <Link
        to={buildPortalUrl(paths.portalArtworkCheck())}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Check artwork
      </Link>

      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          <h1 className="truncate text-xl font-semibold tracking-tight">
            {verdict.filename}
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          {formatBytes(verdict.size_bytes)} · checked{" "}
          {formatLongDateOrDash(verdict.checked_at)}
        </p>
        {/* Act-for attribution (amendment rule 4): the customer sees that
            staff submitted this on their behalf; the staff member's own
            identity never reaches this response, only the fixed string. */}
        {verdict.submitted_by_staff && (
          <p className="text-sm font-medium text-muted-foreground">
            {verdict.attribution_text}
          </p>
        )}
      </div>

      {/* Overall status — blocking must never read as "mostly fine". A file
          with one blocking issue and four auto-fixed ones is not
          four-fifths fine, so this banner is driven entirely by the
          backend's verdict, not by issue counts or proportions. */}
      {outcome === "held" ? (
        <Card className="flex flex-col items-center gap-2 p-8 text-center">
          <Clock className="size-8 text-muted-foreground" />
          <p className="max-w-sm text-lg font-medium">
            {verdict.status_message}
          </p>
        </Card>
      ) : outcome === "passed" ? (
        <Card className="flex flex-col items-center gap-2 border-green-200 bg-green-50 p-8 text-center dark:border-green-900 dark:bg-green-950/20">
          <CheckCircle2 className="size-8 text-green-700 dark:text-green-400" />
          <p className="text-lg font-medium">Good to go</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            We didn&apos;t find anything wrong with this file. It&apos;s ready
            to go straight to print.
          </p>
        </Card>
      ) : outcome === "cant_print" ? (
        <Card className="flex flex-col items-center gap-2 border-red-300 bg-red-50 p-8 text-center dark:border-red-900 dark:bg-red-950/30">
          <XCircle className="size-8 text-red-600 dark:text-red-400" />
          <p className="text-lg font-medium">We can&apos;t print this yet</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Please fix what&apos;s below and upload again — everything else on
            this file is fine.
          </p>
          <Button asChild className="mt-1">
            <Link to={buildPortalUrl(paths.portalArtworkCheck())}>
              Check another file
            </Link>
          </Button>
        </Card>
      ) : (
        <Card className="flex flex-col items-center gap-2 p-8 text-center">
          <CheckCircle2 className="size-8 text-green-700 dark:text-green-400" />
          <p className="text-lg font-medium">We can print this</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {thingsToKnowHeading(
              verdict.issues.filter((i) => i.sentence !== null).length,
            )}{" "}
            Nothing more for you to do.
          </p>
        </Card>
      )}

      <IssueGroupCard
        title="We can't print this"
        icon={XCircle}
        tone="critical"
        issues={sentencesOf(verdict.issues, "cant_print")}
      />
      <IssueGroupCard
        title="We'll sort this for you"
        icon={CheckCircle2}
        tone="positive"
        issues={sentencesOf(verdict.issues, "we_will_sort")}
      />
      <IssueGroupCard
        title="Worth knowing"
        icon={Info}
        tone="neutral"
        issues={sentencesOf(verdict.issues, "worth_knowing")}
      />
    </div>
  );
}
